<template>
  <div class="webauthn-factor">
    
    <sidebar @close="close">
      <template v-slot:head>
        <div class="container has-text-centered">
          <img width="96" height="96" :src="img"/>
          <h2 class="title is-4">
            WebAuthn
          </h2>
        </div>
      </template>
      <template v-slot:body>
        <div class="container has-text-centered" v-if="isAttached">
          <table class="table is-fullwidth width:100%">
            <thead>
            <tr>
              <th>ID</th>
              <th>{{ $t("auth_factors.bind_date") }}</th>
              <th>{{ $t("auth_factors.is_active") }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in webauthn" :key="item.id">
              <td>{{item.id.substring(0,8)}}</td>
              <td>{{ getTimeFormatted(item.lastInitDate) }}</td>
              <td>
                <div v-if="item.blocked" class="has-text-weight-normal label">{{ $t("auth_factors.no") }}</div>
                <div v-else class="has-text-weight-normal label">{{ $t("auth_factors.yes") }}</div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="container has-text-centered" v-else>
          <span>WebAuthn {{ $t("auth_factors.factor_is_not_configured") }}</span>
        </div>
      </template>
      <template v-slot:foot>
        <div class="button is-link is-fullwidth has-text-centered" @click="registerAuthenticator">
          <span>{{ $t("auth_factors.bind_authenticator") }}</span>
        </div>
  <!--        <a @click="registerAuthenticator">{{ $t("auth_factors.bind_authenticator") }}</a>-->

        <br/>
        <div class="container has-text-centered" v-if="isAttached">
          <a @click="reset">{{ $t("auth_factors.reset") }}</a>
        </div>
        <div class="container has-text-centered">
          <p class="help is-danger has-text-left" v-if="invalid">{{ error }}}</p>
        </div>
      </template>
    </sidebar>
  </div>
</template>

<script>
import webauthn from "@/services/webauthn";
import Sidebar from "@/components/Sidebar";

export default {
  name: "WebauthnDetails",
  components: {
    Sidebar,
  },
  props: {
    webauthn: {
      required: true
    },
    img: {
      required: true
    }
  },
  data() {
    return {
      error: "",
      invalid: false,
      lookup: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
    };
  },
  methods: {
    showError(message) {
      this.invalid = true;
      this.error = message;
    },
    bufferDecode: function (value) {
      return Uint8Array.from(atob(this.rfc4846URLtoSTDEncoding(value)), c => c.charCodeAt(0));
    },
    bufferEncode: function (value) {
      return this.uint8ToBase64(value)
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=/g, "");
    },

    encode: function encode(num) {
      return this.lookup.charAt(num)
    },

    tripletToBase64: function (num) {
      return this.encode(num >> 18 & 0x3F) + this.encode(num >> 12 & 0x3F) + this.encode(num >> 6 & 0x3F) + this.encode(num & 0x3F)
    },

    uint8ToBase64: function (uint8) {
      var i
      var extraBytes = uint8.length % 3 // if we have 1 byte left, pad 2 bytes
      var output = ''
      var temp, length

      // go through the array every three bytes, we'll deal with trailing stuff later
      for (i = 0, length = uint8.length - extraBytes; i < length; i += 3) {
        temp = (uint8[i] << 16) + (uint8[i + 1] << 8) + (uint8[i + 2])
        output += this.tripletToBase64(temp)
      }

      // pad the end with zeros, but make sure to not forget the extra bytes
      switch (extraBytes) {
        case 1:
          temp = uint8[uint8.length - 1]
          output += this.encode(temp >> 2)
          output += this.encode((temp << 4) & 0x3F)
          output += '=='
          break
        case 2:
          temp = (uint8[uint8.length - 2] << 8) + (uint8[uint8.length - 1])
          output += this.encode(temp >> 10)
          output += this.encode((temp >> 4) & 0x3F)
          output += this.encode((temp << 2) & 0x3F)
          output += '='
          break
        default:
          break
      }

      return output
    },
    registerAuthenticator() {
      const data = {
        attestation_type: 'none',
        authenticator_attachment: '',
        user_verification: 'preferred',
        resident_key_requirement: false,
        txAuthSimple_extension: ''
      };
      webauthn.challenge(data)
          .then(response => {
            let makeCredentialOptions = response.data;
            if (response.status !== 200) {
              this.showError(makeCredentialOptions);
              return
            }
            makeCredentialOptions.publicKey.challenge = this.bufferDecode(makeCredentialOptions.publicKey.challenge);
            makeCredentialOptions.publicKey.user.id = this.bufferDecode(makeCredentialOptions.publicKey.user.id);
            if (makeCredentialOptions.publicKey.excludeCredentials) {
              for (var i = 0; i < makeCredentialOptions.publicKey.excludeCredentials.length; i++) {
                makeCredentialOptions.publicKey.excludeCredentials[i].id = this.bufferDecode(makeCredentialOptions.publicKey.excludeCredentials[i].id);
              }
            }
            let that = this
            navigator.credentials.create({
              publicKey: makeCredentialOptions.publicKey
            }).then(function (newCredential) {
              that.registerCredential(newCredential);
            }).catch(function (error) {
              that.showError(error.message);
            });
          })
          .catch(error => {
            console.error('There was an error:', error);
          });
    },

    registerCredential(newCredential) {
      let attestationObject = new Uint8Array(newCredential.response.attestationObject);
      let clientDataJSON = new Uint8Array(newCredential.response.clientDataJSON);
      let rawId = new Uint8Array(newCredential.rawId);
      const data = {
        id: newCredential.id,
        rawId: this.bufferEncode(rawId),
        type: newCredential.type,
        response: {
          attestationObject: this.bufferEncode(attestationObject),
          clientDataJSON: this.bufferEncode(clientDataJSON),
        },
      };
      let that = this
      webauthn.register(data)
          .then(resp => {
            if (resp.status >= 200 && resp.status <= 299) {
              that.loadData()
            } else {
              that.showError(that.$t('auth_factors.bind_error'))
            }
          })
          .catch(error => {
            console.error('There was an error::', error);
          });
    },

    close() {
      this.error = "";
      this.invalid = false;
      this.$emit("close");
    },

    loadData() {
      webauthn.getAuthenticators().then(
          resp => {
            this.webauthn = resp.data;
          },
          err => {
            console.log(err);
          }
      );
    },
    // base64 by rfc 4846 uses different 62nd and 63 characters
    rfc4846URLtoSTDEncoding(str){
      str = str.replaceAll('-', '+');
      str = str.replaceAll('_', '/');
      
      return str
    },
    getTimeFormatted(outcomeData) {
      if (!outcomeData || typeof outcomeData === 'undefined') {
        return "—";
      }
      let d = new Date(outcomeData)
      if (!d) {
        return "—"
      }
      return d.toLocaleDateString()
    },

    reset() {
      webauthn.reset(this.$store.state.user.id).then(
          () => {
            this.webauthn = null;
          },
          err => {
            console.log(err);
          }
      );
    },
  },
  computed: {
    isAttached() {
      return typeof this.webauthn !== 'undefined' && this.webauthn && this.webauthn.length > 0
    }
  }
};
</script>
<style>
/* Освободить место для таблицы */
.webauthn-factor .hero-body {
  padding-right: 0;
  padding-left: 0;
}
</style>